import { useCallback, useEffect, useRef, useState } from 'react';
import { ADMIN_USER_ROLE_TIER, type DashboardMetrics } from '@shared/types';
import { useAuthStore } from '../../stores/authStore';
import { useGameStore } from '../../stores/gameStore';
import { adminModuleFor } from '../../games/registry';
import PageHeader from '../../components/PageHeader';
import PageSection from '../../components/PageSection';
import MetricCard from '../../components/MetricCard';
import Breakdown from '../../components/Breakdown';
import { getDashboardMetrics } from '../../services/dashboard';
import { ApiError } from '../../services/api';

/** Compact integer formatting with thousands separators. */
function n(value: number): string {
  return value.toLocaleString();
}

/** USD formatting from a dollar amount. */
function usd(value: number): string {
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
}

/**
 * Sub-label for the revenue card. The headline figure is gross catalog list
 * price for PRODUCTION transactions only, so the excluded buckets are named
 * here — an operator who sees "$0.00" needs to know 5 sandbox purchases are
 * sitting behind it rather than assume the metric is broken.
 */
function iapRevenueSub(iap: DashboardMetrics['iap']): string {
  const excluded: string[] = [];
  if (iap.sandboxCount > 0) excluded.push(`${n(iap.sandboxCount)} sandbox`);
  if (iap.unknownEnvCount > 0) {
    excluded.push(`${n(iap.unknownEnvCount)} unclassified`);
  }
  const base = 'lifetime list price, non-refunded';
  return excluded.length > 0 ? `${base} · excl. ${excluded.join(' · ')}` : base;
}

function fmtTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * Admin overview dashboard (#452). Point-in-time database + live operational
 * metrics. Read-only; a manual Refresh re-fetches `/admin/dashboard/metrics`.
 */
export default function DashboardPage() {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; data: DashboardMetrics }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  // The purchases drill-down is admin-tier (identified buyer rows), while this
  // dashboard is viewer-tier. Rendering a link a viewer cannot follow would
  // bounce them off their own landing page, so the IAP tiles stay static for
  // them — the aggregate figures they can already see are unaffected.
  const role = useAuthStore((s) => s.adminUser?.role);
  const canSeePurchases =
    role !== undefined &&
    ADMIN_USER_ROLE_TIER[role] >= ADMIN_USER_ROLE_TIER.admin;

  const activeGameId = useGameStore((s) => s.activeGameId);
  const gameSections = adminModuleFor(activeGameId)?.dashboardSections ?? [];

  const reqCounterRef = useRef(0);
  const fetchMetrics = useCallback(async () => {
    const reqId = ++reqCounterRef.current;
    setState({ status: 'loading' });
    try {
      const data = await getDashboardMetrics();
      if (reqId !== reqCounterRef.current) return;
      setState({ status: 'ready', data });
    } catch (err) {
      if (reqId !== reqCounterRef.current) return;
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load dashboard metrics';
      setState({ status: 'error', message });
    }
  }, []);

  useEffect(() => {
    void fetchMetrics();
  }, [fetchMetrics]);

  const data = state.status === 'ready' ? state.data : null;
  const loading = state.status === 'loading';

  return (
    <div className="space-y-4" data-testid="dashboard-screen">
      <PageHeader
        title="Dashboard"
        subtitle={
          data ? `Updated ${fmtTimestamp(data.generatedAt)}` : undefined
        }
        actions={
          <button
            type="button"
            onClick={() => void fetchMetrics()}
            disabled={loading}
            data-testid="dashboard-refresh"
            className="text-sm rounded border border-slate-300 bg-white px-3 py-1.5 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        }
        testId="dashboard-header"
      />

      {state.status === 'error' && (
        <div
          role="alert"
          data-testid="dashboard-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
        >
          {state.message}
        </div>
      )}

      {loading && !data && (
        <div
          className="text-sm text-slate-500"
          data-testid="dashboard-loading"
        >
          Loading metrics…
        </div>
      )}

      {data && (
        <>
          {/* Live activity — the "right now" row. */}
          <PageSection
            title="Live activity"
            inset={false}
            testId="dashboard-section-live"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <MetricCard
                label="Online players"
                value={n(data.live.onlineUsers)}
                sub={`${n(data.live.onlineBots)} bots online (excluded)`}
                tone="positive"
                testId="metric-online-users"
                to="/users?online=true&bots=false"
                linkLabel={`View the ${n(data.live.onlineUsers)} players online now`}
              />
              <MetricCard
                label="Active games"
                value={n(data.live.activeMatches)}
                sub="matches in progress"
                tone="accent"
                testId="metric-active-matches"
                to="/matches?status=in_progress"
                linkLabel={`View the ${n(data.live.activeMatches)} matches in progress`}
              />
              <MetricCard
                label="In matchmaking"
                value={n(data.live.matchmakingQueue)}
                sub="waiting for an opponent"
                testId="metric-matchmaking-queue"
                to="/matchmaking"
                linkLabel={`View the ${n(data.live.matchmakingQueue)} players waiting for an opponent`}
              />
            </div>
          </PageSection>

          {/* Users. */}
          <PageSection
            title="Users"
            inset={false}
            testId="dashboard-section-users"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <MetricCard
                label="Total players"
                value={n(data.users.total)}
                sub={`${n(data.users.guests)} guest · ${n(
                  data.users.social,
                )} social`}
                testId="metric-users-total"
                to="/users?bots=false"
                linkLabel={`Browse all ${n(data.users.total)} players`}
              />
              <MetricCard
                label="New (24h)"
                value={n(data.users.newLast24h)}
                sub={`${n(data.users.newLast7d)} in last 7d`}
                tone="positive"
                testId="metric-users-new"
                to="/users?since=24h&bots=false"
                linkLabel={`View the ${n(data.users.newLast24h)} accounts created in the last 24 hours`}
              />
              <MetricCard
                label="Banned"
                value={n(data.users.banned)}
                tone={data.users.banned > 0 ? 'danger' : 'default'}
                testId="metric-users-banned"
                to="/users?banned=true&bots=false"
                linkLabel={`View the ${n(data.users.banned)} banned players`}
              />
              <MetricCard
                label="Bots"
                value={n(data.users.bots)}
                sub="excluded from player counts"
                testId="metric-users-bots"
                to="/users?bots=true"
                linkLabel={`View the ${n(data.users.bots)} bot accounts`}
              />
            </div>
          </PageSection>

          {/* First-session funnel (#764). */}
          {data.firstSession && (
            <PageSection
              title={`First session (last ${data.firstSession.windowDays}d)`}
              inset={false}
              testId="dashboard-section-first-session"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <MetricCard
                  label="Signed up"
                  value={n(data.firstSession.signedUp)}
                  sub={`${n(data.firstSession.completedMatch)} finished a match · ${n(
                    data.firstSession.returnedNextDay,
                  )} active 24h+ after signup`}
                  testId="metric-first-session-signed-up"
                />
                <MetricCard
                  label="Started a match"
                  value={n(data.firstSession.startedMatch)}
                  sub={`${n(data.firstSession.madeFirstMove)} made a first move`}
                  testId="metric-first-session-started"
                />
                <MetricCard
                  label="Time to first move"
                  value={
                    data.firstSession.medianFirstMoveMs === null
                      ? '—'
                      : `${Math.round(data.firstSession.medianFirstMoveMs / 1000)}s`
                  }
                  sub="median, first match"
                  testId="metric-first-session-first-move"
                />
                <MetricCard
                  label="Dropped mid-match"
                  value={n(data.firstSession.backgroundedInMatch)}
                  sub={`backgrounded · ${n(data.firstSession.leftMatch)} left a match`}
                  tone={data.firstSession.backgroundedInMatch > 0 ? 'danger' : 'default'}
                  testId="metric-first-session-dropped"
                />
              </div>
            </PageSection>
          )}

          {/* The active game's own sections (#817 — its admin manifest's
              `dashboardSections`, e.g. Word Clash's question bank). */}
          {gameSections.map((Section, i) => (
            <Section key={i} metrics={data} />
          ))}

          {/* In-app purchases. */}
          <PageSection
            title="In-app purchases"
            inset={false}
            testId="dashboard-section-iap"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <MetricCard
                label="Revenue (USD)"
                value={usd(data.iap.revenueUsd)}
                sub={iapRevenueSub(data.iap)}
                tone="positive"
                testId="metric-iap-revenue"
                to={canSeePurchases ? '/purchases?environment=production' : undefined}
                linkLabel="View the production purchases behind this revenue figure"
              />
              <MetricCard
                label="Transactions"
                value={n(data.iap.total)}
                sub={`${n(data.iap.countLast24h)} in 24h · ${n(
                  data.iap.countLast7d,
                )} in 7d`}
                testId="metric-iap-total"
                to={canSeePurchases ? '/purchases?status=live' : undefined}
                linkLabel={`Browse all ${n(data.iap.total)} purchases`}
              />
              <MetricCard
                label="Refunded"
                value={n(data.iap.refunded)}
                tone={data.iap.refunded > 0 ? 'danger' : 'default'}
                testId="metric-iap-refunded"
                to={canSeePurchases ? '/purchases?status=refunded' : undefined}
                linkLabel={`View the ${n(data.iap.refunded)} refunded purchases`}
              />
              <MetricCard
                label="By platform"
                value={
                  <Breakdown
                    entries={data.iap.byPlatform}
                    testId="metric-iap-by-platform"
                  />
                }
                testId="metric-iap-platform"
              />
            </div>
          </PageSection>
        </>
      )}
    </div>
  );
}
